// Shows AI-generated suggestion vs current value
// Allows user to replace current value or keep existing
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useFieldMetadata } from '../context/FieldMetadataContext';

interface AIComparisonPanelProps {
  fieldName: string;
  currentValue: string;
}

export function AIComparisonPanel({ fieldName, currentValue }: AIComparisonPanelProps) {
  const { getMetadata, setAISuggestion, setStatus } = useFieldMetadata();
  const { setValue } = useFormContext();
  const { t } = useTranslation('common');

  const metadata = getMetadata(fieldName);
  const suggestion = metadata.aiSuggestion;

  if (!suggestion) return null;

  const handleReplace = () => {
    // Replace current value with AI suggestion
    setValue(fieldName, suggestion, { shouldDirty: true, shouldValidate: true });
    // Clear suggestion and set status to validated
    setAISuggestion(fieldName, null);
    setStatus(fieldName, 'validated');
  };

  const handleKeep = () => {
    // Keep current value: clear the suggestion and restore the status the
    // field had before the AI suggestion arrived, instead of forcing 'validated'
    setAISuggestion(fieldName, null);
    setStatus(fieldName, metadata.previousStatus ?? 'in_progress');
  };

  return (
    <div className="ai-comparison-panel bg-[#19A2F8]/10 rounded-lg p-4 space-y-4 border border-[#19A2F8]/20">
      {/* Header */}
      <div className="flex items-center gap-2 text-sm font-medium text-[#19A2F8]">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
        {t('ai.compareAnswers')}
      </div>

      {/* Current value */}
      {currentValue && (
        <div className="space-y-1.5">
          <label className="text-xs text-gray-400 font-medium">{t('ai.currentValue')}</label>
          <div className="p-3 bg-white/5 rounded-lg border border-white/10 text-sm text-gray-300 whitespace-pre-wrap">
            {currentValue}
          </div>
        </div>
      )}

      {/* AI suggestion */}
      <div className="space-y-1.5">
        <label className="text-xs text-[#19A2F8] font-medium">{t('ai.aiSuggestion')}</label>
        <div className="p-3 bg-[#19A2F8]/10 rounded-lg border border-[#19A2F8]/30 text-sm text-gray-200 whitespace-pre-wrap">
          {suggestion}
        </div>
      </div>

      {/* Actions - Figma: Keep (ghost) + Replace (primary) */}
      <div className="flex gap-2 justify-end pt-2">
        <button
          type="button"
          onClick={handleKeep}
          className="px-4 py-2 text-sm font-medium text-gray-300 hover:bg-white/10 rounded-lg transition-colors"
        >
          {t('ai.keepCurrent')}
        </button>
        <button
          type="button"
          onClick={handleReplace}
          className="px-4 py-2 text-sm font-medium text-white bg-[#19A2F8] hover:bg-[#19A2F8]/80 rounded-lg transition-colors"
        >
          {t('ai.replace')}
        </button>
      </div>
    </div>
  );
}
